import { unzipSync, zipSync, strFromU8, type Zippable } from "fflate";

export interface DeployFile {
  /** POSIX path relative to the pack root, e.g. "manifest.json" or "scripts/main.js". */
  path: string;
  data: Uint8Array;
}

export interface DeployManifest {
  project: string;
  branch: string;
  commitSha: string;
  deployFolderName: string;
  bpFileCount?: number;
  rpFileCount?: number;
}

export interface ParsedArtifact {
  manifest: DeployManifest;
  bpFiles: DeployFile[];
  rpFiles: DeployFile[];
}

/**
 * Parse the deploy artifact zip produced by the daemon. Entries:
 *   - deploy-manifest.json
 *   - bp/<...>   -> behavior pack files (prefix stripped)
 *   - rp/<...>   -> resource pack files (prefix stripped)
 */
export function parseArtifact(buf: ArrayBuffer): ParsedArtifact {
  const files = unzipSync(new Uint8Array(buf));

  const manifestRaw = files["deploy-manifest.json"];
  if (!manifestRaw) {
    throw new Error("artifact is missing deploy-manifest.json");
  }
  let manifest: DeployManifest;
  try {
    manifest = JSON.parse(strFromU8(manifestRaw)) as DeployManifest;
  } catch {
    throw new Error("artifact deploy-manifest.json is not valid JSON");
  }
  if (!manifest.deployFolderName) {
    throw new Error("artifact manifest has no deployFolderName");
  }

  const bpFiles: DeployFile[] = [];
  const rpFiles: DeployFile[] = [];
  for (const [key, data] of Object.entries(files)) {
    if (key === "deploy-manifest.json") continue;
    if (key.endsWith("/")) continue; // directory entry, if any
    if (key.startsWith("bp/")) {
      bpFiles.push({ path: key.slice(3), data });
    } else if (key.startsWith("rp/")) {
      rpFiles.push({ path: key.slice(3), data });
    }
    // ignore anything else defensively
  }

  if (bpFiles.length === 0 && rpFiles.length === 0) {
    throw new Error("artifact contains no behavior or resource pack files");
  }
  return { manifest, bpFiles, rpFiles };
}

/**
 * Repackage a parsed artifact into a standard .mcaddon blob (zip with two pack
 * folders, each holding its own manifest.json, and NO deploy-manifest.json).
 * Used by the non-Chromium fallback so the user can import it manually.
 */
export function buildMcaddonBlob(parsed: ParsedArtifact): Blob {
  const files: Zippable = {};
  for (const f of parsed.bpFiles) files[`${parsed.manifest.deployFolderName}_bp/${f.path}`] = f.data;
  for (const f of parsed.rpFiles) files[`${parsed.manifest.deployFolderName}_rp/${f.path}`] = f.data;
  const zipped = zipSync(files, { level: 6 });
  return new Blob([new Uint8Array(zipped)], { type: "application/zip" });
}
